import { computed, Directive, effect, ElementRef, HostListener, inject, Input, Renderer2, signal } from "@angular/core";

import { AdaaHelper } from "../../core/utils";
import { Language } from "../constants/enums";
import { MandatoryFields } from "../models/mandatory-fields-model";
import { SmartToolTipsService } from "../services/smart-tooltips-service";

@Directive({
  selector: "[adaaSmartToolTips]",
})
export class SmartToolTipsDirective {
  private _smartToolTipsService = inject(SmartToolTipsService);
  private _el = inject(ElementRef);
  private _renderer = inject(Renderer2);

  private readonly _toolTip = signal<string>("");
  private readonly _toolTipName = signal<string | undefined>(undefined);
  private readonly _parameterCatalogs = signal<MandatoryFields["parameterCatalogs"]>([]);

  private _toolTipModalElement: HTMLElement | null;
  private _toolTipElement: HTMLElement | null;
  private _firstRun = true;
  private _isToolTipCreated = false;
  private _isModalCreated = false;

  @Input() public set adaaSmartToolTips(toolTipName: string | undefined) {
    this._toolTipName.set(toolTipName);
  }

  private readonly _catalog = computed(() =>
    this._parameterCatalogs().find((e) => e.fieldName === this._toolTipName())
  );

  @HostListener("document:click", ["$event"])
  public onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;

    if (target.closest(".info-modal")) return;
    else {
      if (!this._toolTipModalElement) return;

      this._toolTipModalElement.style.display = "none";
      this._isModalCreated = false;
    }
  }

  cleanupEffect = effect(() => {
    const enabled = this._smartToolTipsService.isSmartToolTipsEnabled();
    this._parameterCatalogs.set(this._smartToolTipsService.toolTips());

    if (this._firstRun) {
      this._firstRun = false;
      return;
    } else {
      enabled ? this._createToolTip() : this._removeToolTip();
    }
  });

  private _createToolTip() {
    if (!this._catalog()) return;

    const desc = AdaaHelper.getItemValueByToken(this._catalog(), "description");
    this._toolTip.set(desc);

    if (this._isToolTipCreated) return;
    this._isToolTipCreated = true;

    const tooltip = this._renderer.createElement("span");
    const icon = this._renderer.createElement("i");

    this._renderer.addClass(icon, "adaa-icon-console_help");
    this._renderer.appendChild(tooltip, icon);

    this._renderer.addClass(tooltip, "smart-tooltip");
    this._renderer.addClass(tooltip, AdaaHelper.getCurrentLang() === Language.Arabic ? "ae" : "en");

    this._renderer.listen(tooltip, "click", () => {
      if (this._isModalCreated) return;

      const infoModal = this._renderer.createElement("span");
      const text = this._renderer.createText(this._toolTip());

      this._renderer.appendChild(infoModal, text);
      this._renderer.addClass(infoModal, "info-modal");
      this._renderer.addClass(infoModal, AdaaHelper.getCurrentLang() === Language.Arabic ? "ae" : "en");
      this._renderer.insertBefore(parent, infoModal, null);
      this._toolTipModalElement = infoModal;

      setTimeout(() => {
        this._isModalCreated = true;
        this._toolTipModalElement!.style.display = "block";
      }, 0);
    });

    const parent = this._el.nativeElement.parentNode;
    this._toolTipElement = tooltip;
    this._renderer.addClass(parent, "position-relative");

    const nextSibling = this._el.nativeElement.nextSibling;
    this._renderer.insertBefore(parent, tooltip, nextSibling);
  }

  private _removeToolTip() {
    if (!this._el?.nativeElement || !this._toolTipElement) return;

    const nativeEl = this._el.nativeElement;
    const parent = nativeEl.parentNode;

    if (parent && this._toolTipElement) {
      this._renderer.removeChild(parent, this._toolTipElement);
      this._toolTipElement = null;
      this._toolTipModalElement = null;
      this._isToolTipCreated = false;
    }
  }
}
